import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';

import {merge} from '#core/utils/config-utils.js';
import {flopShadow, resolveBackground} from '#core/utils/color-utils.js';
import {buildIntentsFromConfig} from '#core/utils/intent-factory.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';
import {Betslip} from '#core/application/modules/betting-center/betslip/betslip.js';
import {generateDotNotification} from '#core/application/modules/tiles/dot-notification/index.js';

import {Interface} from '../interface.js';

import PALETTE_SCHEMA from './mobile-nav-bar.palette-schema.yaml';
import SCHEMA from './mobile-nav-bar.schema.yaml';

const {NOTIFICATIONS} = MojitoModules.QuickLinks.types;
const {NAVIGATION} = MojitoPresentation.Base.Navigation.types;

class MobileNavBarFeature extends AbstractVisualFeature {
    get name() {
        return 'Mobile NavBar';
    }

    get configSchema() {
        return SCHEMA;
    }

    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    getLinksLayoutConfig() {
        const useGroupedLayout = !Betslip.isFloating;

        if (useGroupedLayout) {
            const groupedLinksContainer = {
                style: {
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 'calc((100% - 80px) / 2)',
                    minWidth: 0,
                },
            };

            return {
                linksContainer: groupedLinksContainer,
                rightLinksContainer: groupedLinksContainer,
            };
        }

        return {
            linksContainer: Betslip.isFloating
                ? {
                      style: {
                          gridTemplateColumns: 'repeat(auto-fit, minmax(50px, 1fr))',
                      },
                  }
                : {
                      nthSeparator: {
                          1: null,
                      },
                      style: {
                          gridTemplateColumns: '1fr 1fr 80px 1fr 1fr',
                      },
                  },
        };
    }

    getSerializableVisualConfig(PALETTE) {
        return {
            QuickLinks: {
                isDotNotificationVisible: true,
                dotNotification: merge(generateDotNotification(PALETTE.DOT_NOTIFICATION), {
                    style: {
                        container: {
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                        },
                    },
                }),
                container: {
                    style: {
                        height: 48,
                        boxShadow: flopShadow(PALETTE.CONTAINER_SHADOW),
                        ...resolveBackground(PALETTE.CONTAINER_BG),
                        ...(PALETTE.CONTAINER_BORDER_RADIUS > 0
                            ? {
                                  borderRadius: `${PALETTE.CONTAINER_BORDER_RADIUS}px ${PALETTE.CONTAINER_BORDER_RADIUS}px 0 0`,
                              }
                            : {}),
                    },
                },
                ...this.getLinksLayoutConfig(),
                linkContainer: {
                    itemSpacing: 4,
                },
                // better to leave it like it is now
                linkIcon: {
                    style: {
                        minHeight: 16,
                        minWidth: 16,
                        fill: PALETTE.ICON_COLOR,
                    },
                },
                linkLabel: {
                    style: {
                        whiteSpace: 'nowrap',
                        ...PALETTE.LABEL_TEXT,
                    },
                },
                liveCountBadge: {
                    container: {
                        style: {
                            borderRadius: '50%',
                            ...resolveBackground(PALETTE.LIVE_EVENTS_LABEL_BG),
                        },
                    },
                    text: {
                        style: {
                            lineHeight: 1,
                            ...PALETTE.LIVE_EVENTS_LABEL_TEXT,
                        },
                    },
                },
                links: this.getQuickLinksConfig(PALETTE),
            },
        };
    }

    getQuickLinksConfig(PALETTE) {
        const links = buildIntentsFromConfig(this.config.mobileNavigationBarItems);
        const useGroupedLayout = !Betslip.isFloating;
        const linkStyleOverrides = PALETTE?.LINK_STYLE_OVERRIDES;

        return links.map((link, index) => {
            if (useGroupedLayout) {
                link.isRightAligned = index >= 2;
            }

            if (link.intent.type === MojitoPresentation.Base.Intent.Types.SHOW_SLIDING_OPEN_BETS) {
                link.notification = {
                    onActions: [NOTIFICATIONS.HAS_OPEN_BETS],
                };
            }

            if (
                link.intent.type === MojitoPresentation.Base.Intent.Types.VIEW &&
                link.intent.data?.type === NAVIGATION.INPLAY_PAGE
            ) {
                link.showLiveCount = true;
            }

            if (linkStyleOverrides?.[link.label]) {
                return merge(link, linkStyleOverrides[link.label]);
            }

            return link;
        });
    }
}

export const MobileNavBar = new MobileNavBarFeature(Interface);
